import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Sparkles,
  Check,
  Copy,
  ArrowUpRight,
  Loader2,
  X,
} from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useSubscription } from "@/context/SubscriptionContext";

export default function HookAnalysis() {
  const navigate = useNavigate();
  const {
    subscription,
    isTrial,
    isAdmin,
    openPaywall,
    refreshSubscription,
  } = useSubscription();

  const [hookText, setHookText] = useState("");
  const [topic, setTopic] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [creatingScriptIndex, setCreatingScriptIndex] = useState(null);
  const textareaRef = useRef(null);

  const hooksLimit = subscription?.quotas?.hooks || (isTrial ? 5 : 0);
  const hooksUsed = subscription?.usage?.hooks || 0;
  const hooksRemaining = Math.max(0, hooksLimit - hooksUsed);
  const canAnalyze = isAdmin || hooksRemaining > 0;

  const words = hookText.trim() ? hookText.trim().split(/\s+/) : [];
  const wordCount = words.length;

  const handleAnalyze = async () => {
    if (!canAnalyze) {
      openPaywall("Hook Analysis");
      return;
    }

    if (!hookText.trim()) {
      toast.error("Enter a hook to analyze");
      textareaRef.current?.focus();
      return;
    }

    setAnalyzing(true);
    try {
      const { data } = await api.post("/billing/hook-analysis", {
        hook: hookText.trim(),
        topic: topic.trim() || undefined,
      });
      setResult(data.analysis);
      refreshSubscription();
    } catch (err) {
      if (err.response?.status === 402) {
        openPaywall("Hook Analysis");
      }
      toast.error(apiErrorMessage(err));
    } finally {
      setAnalyzing(false);
    }
  };

  const handleKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      if (!analyzing && hookText.trim()) {
        handleAnalyze();
      }
    }
  };

  const copyToClipboard = (text, index) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopiedIndex(null), 1800);
  };

  const handleUseInScript = async (hook, index) => {
    setCreatingScriptIndex(index);
    try {
      const title = hook.length > 60 ? `${hook.slice(0, 57)}...` : hook;
      const body = `Hook:\n"${hook}"\n\nOutline:\n`;
      const { data } = await api.post("/content", {
        title,
        body,
        platforms: ["youtube", "instagram"],
      });
      toast.success("Draft created");
      navigate(`/content/${data.id}`);
    } catch (err) {
      toast.error(apiErrorMessage(err) || "Failed to create draft");
    } finally {
      setCreatingScriptIndex(null);
    }
  };

  return (
    <AppShell title="Hook Analyzer">
      <div className="mx-auto max-w-3xl pb-16">
        {/* Header */}
        <div className="flex items-center justify-between pb-6 border-b border-border mb-6">
          <div>
            <h1 className="text-2xl font-bold font-display text-foreground">
              Hook Analyzer
            </h1>
            <p className="text-xs text-muted-foreground mt-1">
              Ranked by your Creator DNA and real evidence.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Quota:</span>
            <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-elevated border border-border text-foreground">
              {isAdmin ? "Unlimited" : `${hooksRemaining} left`}
            </span>
          </div>
        </div>

        {/* Input Card */}
        <div className="rounded-2xl border border-border bg-surface p-5 shadow-sm">
          <div className="relative">
            <textarea
              ref={textareaRef}
              value={hookText}
              onChange={(e) => setHookText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Paste or write your opening 1-2 spoken sentences..."
              rows={3}
              className="w-full resize-none rounded-xl border border-border bg-background p-3.5 text-sm text-foreground placeholder:text-muted-foreground/60 outline-none focus:border-accent focus:ring-1 focus:ring-accent leading-relaxed"
            />
            {hookText && (
              <button
                type="button"
                onClick={() => setHookText("")}
                className="absolute top-2.5 right-2.5 p-1 text-muted-foreground hover:text-foreground rounded"
                title="Clear"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          <div className="mt-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3 w-full sm:w-auto">
              <Input
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Topic / Niche (optional)"
                className="h-9 flex-1 sm:w-48 text-xs rounded-lg border-border bg-background"
              />
              <span className="text-xs text-muted-foreground font-mono shrink-0">
                {wordCount} {wordCount === 1 ? "word" : "words"}
              </span>
            </div>

            <Button
              onClick={handleAnalyze}
              disabled={analyzing || !hookText.trim()}
              className="h-9 w-full sm:w-auto rounded-lg bg-accent px-5 text-xs font-semibold text-white hover:bg-accent-hover transition-all disabled:opacity-50"
            >
              {analyzing ? (
                <>
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Sparkles className="mr-1.5 h-3.5 w-3.5" />
                  Analyze Hook
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Analysis Results */}
        {result && !analyzing && (
          <div className="mt-6 space-y-4 animate-in fade-in duration-200">
            {/* Score & Verdict Card */}
            <div className="rounded-2xl border border-border bg-surface p-5 shadow-sm">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-accent">
                    {result.hook_type || "Analysis"}
                  </span>
                  <p className="mt-1.5 text-sm font-medium text-foreground leading-relaxed">
                    {result.verdict}
                  </p>
                </div>

                <div className="flex flex-col items-center justify-center rounded-xl bg-background border border-border px-4 py-2.5 text-center shrink-0 min-w-[80px]">
                  <span className="text-[10px] uppercase font-bold text-muted-foreground">
                    Score
                  </span>
                  <span
                    className={`text-2xl font-black ${
                      result.score >= 80
                        ? "text-emerald-600"
                        : result.score >= 65
                        ? "text-amber-500"
                        : "text-rose-500"
                    }`}
                  >
                    {result.score}
                  </span>
                </div>
              </div>

              {/* Concise Strengths & Weaknesses */}
              {(result.strengths?.length > 0 || result.weaknesses?.length > 0) && (
                <div className="mt-4 pt-4 border-t border-border grid sm:grid-cols-2 gap-4 text-xs">
                  {result.strengths?.length > 0 && (
                    <div>
                      <span className="font-semibold text-foreground block mb-1.5">
                        Strengths
                      </span>
                      <ul className="space-y-1 text-muted-foreground">
                        {result.strengths.map((s, i) => (
                          <li key={i} className="flex items-start gap-1.5">
                            <Check className="h-3.5 w-3.5 text-emerald-600 shrink-0 mt-0.5" />
                            <span>{s}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {result.weaknesses?.length > 0 && (
                    <div>
                      <span className="font-semibold text-foreground block mb-1.5">
                        To Improve
                      </span>
                      <ul className="space-y-1 text-muted-foreground">
                        {result.weaknesses.map((w, i) => (
                          <li key={i} className="flex items-start gap-1.5">
                            <span className="text-amber-500 font-bold shrink-0">•</span>
                            <span>{w}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Rewritten Alternatives */}
            {result.alternatives?.length > 0 && (
              <div className="rounded-2xl border border-border bg-surface p-5 shadow-sm">
                <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3">
                  Suggested Rewrites
                </h3>

                <div className="space-y-2.5">
                  {result.alternatives.map((alt, idx) => (
                    <div
                      key={idx}
                      className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3.5 rounded-xl border border-border bg-background hover:border-accent/30 transition-colors"
                    >
                      <p className="text-sm font-medium text-foreground leading-snug">
                        "{alt.hook}"
                      </p>

                      <div className="flex items-center gap-2 shrink-0 self-end sm:self-auto">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => copyToClipboard(alt.hook, idx)}
                          className="h-7 text-xs rounded-lg px-2.5"
                        >
                          {copiedIndex === idx ? (
                            <>
                              <Check className="h-3 w-3 mr-1 text-emerald-600" />
                              Copied
                            </>
                          ) : (
                            <>
                              <Copy className="h-3 w-3 mr-1" />
                              Copy
                            </>
                          )}
                        </Button>

                        <Button
                          size="sm"
                          onClick={() => handleUseInScript(alt.hook, idx)}
                          disabled={creatingScriptIndex === idx}
                          className="h-7 text-xs rounded-lg px-3 bg-accent text-white hover:bg-accent-hover"
                        >
                          <ArrowUpRight className="h-3 w-3 mr-1" />
                          {creatingScriptIndex === idx ? "Creating..." : "Use in Script"}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </AppShell>
  );
}
